// @flow
import './Breadcrumb.css';

import * as React from 'react';
import _ from 'lodash';
import StoreListener from '../store/StoreListener';
import { PWD, BREADCRUMB_ASSETS, ALL_FILES_OPTION, BREADCRUMB_COLLECTIONS, PWD_COLLECTIONS, COLLECTION_ID, SELECTED_COLLECTION } from '../store/Constants';
import { ZmCollection, RepoItem, LsZmCollectionsResponse } from '../autogenerated/ZoomBridge';
import Button from './Button';
import ViewUtil from '../ViewUtil';
import AssetListCache from '../queries/asset/AssetListCache';
import Store from '../store/Store';
import t from '../i18n/I18n';
import { ThemeSetup } from '../themes/ThemeSetup';
import CollectionUtil from '../queries/lightbox/CollectionUtil';
import { TextWithEllipsisFileExtension } from './TextWithEllipsis';
import ToolTip from './AssetPropertyView';


const debug = require('../debug/debug')('wc:common:Breadcrumb');

type Props = {
  collection?: ZmCollection,
  showFolderNavView?: boolean,
  PWD?: number[],
  BREADCRUMB_ASSETS?: RepoItem[],
  pwd_collections?: number[],
  BREADCRUMB_COLLECTIONS?: ZmCollection[] //Just so that when STore gets updated as a prop it get rerender.
};

const storeItems = [PWD, BREADCRUMB_ASSETS, PWD_COLLECTIONS, BREADCRUMB_COLLECTIONS];
//$FlowFixMe
@StoreListener(storeItems)
export default class Breadcrumb extends React.Component<Props> {
  _assetCache: AssetListCache = undefined;

  constructor(props) {
    super(props);
    this._assetCache = AssetListCache.getInstance();
  }

  componentDidMount() {
    const { collection } = this.props;
    if (collection) {
      let pwd_collections = ViewUtil.getCollectionPwd();
      const selectedCollection = Store.get(SELECTED_COLLECTION);
      if (selectedCollection && (!pwd_collections || !pwd_collections.length) && ViewUtil.isAllSubTabActive()) {
        pwd_collections = [selectedCollection.getCollectionId()];
      }
      this.fetchCollections(pwd_collections);
    }
    else {
      this.fetchAssets(ViewUtil.getPwd());
    }
  }

  componentDidUpdate(prevProps: Props) {
    //console.log(ViewUtil.getCollectionPwd());
    const { PWD, pwd_collections, collection } = this.props;
    if (collection && !_.isEqual(pwd_collections, prevProps.pwd_collections)) {
      this.fetchCollections(pwd_collections);
    }
    else if (!_.isEqual(PWD, prevProps.PWD)) {
      this.fetchAssets(PWD);
    }
  }

  fetchAssets = async (fuids: number[] = []) => {
    try {
      if (!(fuids && fuids.length)) {
        if (_.size(this.props.BREADCRUMB_ASSETS) > 0) {
          Store.put(BREADCRUMB_ASSETS, []);
        }
        return;
      }

      let parentFuid = 0;
      const promises = fuids.map((fuid: number, i: number) => {
        const promise = this.fetchAsset(parentFuid, fuid, i);
        parentFuid = fuid;
        return promise;
      });

      let assets = await Promise.all(promises);
      assets = _.compact(assets);

      const resultFuids = assets.map((a) => a.getFuid());
      if (!_.isEqual(fuids, resultFuids)) {
        debug('[fetchAssets()] mismatch between fuids and resultFuids: ', { fuids, resultFuids });
        debug('[fetchAssets()] setting pwd: ', resultFuids);
        ViewUtil.setPwd(resultFuids);
      }

      debug('[fetchAssets()] assets: ', assets);
      Store.put(BREADCRUMB_ASSETS, assets);
    } catch (err) {
      debug('[fetchAssets()] error: ', err);
    }
  };

  fetchAsset = async (parentFuid: number, fuid: number, i: number): RepoItem => {
    debug(`[fetchAsset() ${i}] args: `, { parentFuid, fuid });

    // We already have the target fuid but the backend requires us to run
    // ListFilesQuery before we can run GetAssetsInfoQuery.
    // const fuids = await this._assetCache.fetchFuids(parentFuid, {
    //   /* force: true, nocache: true */
    // }); // nocache because some case it cache fuids for display assets.
    // if (!fuids.includes(fuid)) {
    //   debug(`[fetchAsset() ${i}] parent folder ${parentFuid} does not contain asset ${fuid}`);
    //   return null;
    // }

    const assets = await this._assetCache.getOrFetchRepoItems(parentFuid, [fuid]);
    const asset = assets.find((a) => a.getFuid() === fuid);
    debug(`[fetchAsset() ${i}] asset: `, asset);

    return asset;
  };

  onBaseClick = () => {
    ViewUtil.setPwd(null);
  };

  onClick = (i: ?number) => {
    ViewUtil.popPwd(i);
  };

  fetchCollection = async (collectionId: number, i: number) => {
    const collectionResp: LsZmCollectionsResponse = await CollectionUtil.getCollectionById(collectionId);
    const collection = collectionResp ? collectionResp.getCollections().find(c => c.getCollectionId() === collectionId) : null;
    debug(`[fetchCollection() ${i}] collection: `, collection);

    return collection;
  };

  fetchCollections = async (collectionIds: Array = []) => {
    Store.put(BREADCRUMB_COLLECTIONS, []);
    if (!collectionIds || (collectionIds && collectionIds.length <= 0)) {
      return;
    }

    const promises = collectionIds.map((cid: number, i: number) => {
      const promise = this.fetchCollection(cid, i);
      return promise;
    });

    let crumbCollections = await Promise.all(promises);
    crumbCollections = _.compact(crumbCollections);
    Store.put(BREADCRUMB_COLLECTIONS, crumbCollections);
  };

  renderCrumb = (fuid: number, label: string, i: ?number) => {
    //const { PWD: pwd = [], collection } = this.props;
    const { collection } = this.props;
    const pwd = ViewUtil.getPwd();
    const isLast = i === pwd.length - 1;
    const props: Object = {
      key: fuid,
      className: 'crumb',
      icon: isLast || collection ? ThemeSetup.getImageURL('folder_open_black.svg') : 'images/folder_open_gray.svg'
    };

    if (!isLast) {
      props.onClick = () => this.onClick(i);
    }
    return (
      <div className='crumb' onClick={props.onClick} key={fuid + 'crumb'}>
        <div className='crumb-icon'><img src={props.icon}></img></div>
        <ToolTip className='tooltip-crumb' text={label} >
          <div className='crumb-label'>{label}</div>
        </ToolTip>
      </div>
    )
  };

  renderCrumbs = () => {
    // const { PWD: pwd = [], BREADCRUMB_ASSETS: assets } = this.props;
    const { BREADCRUMB_ASSETS: assets } = this.props;
    const pwd = ViewUtil.getPwd();
    const crumbs = pwd.map((fuid, i) => {
      const asset: RepoItem = assets ? assets.find((a) => a.getFuid() === fuid) : null;
      if (!asset) {
        return [];
      }

      const sep = (
        <span key={-i - 1} className="separator">
          <img src="images/arrow_down.svg" />
        </span>
      );

      const crumb = this.renderCrumb(fuid, asset.getName(), i);

      return [sep, crumb];
    });

    return _.flatten(crumbs);
  };

  onBaseCollClick = async () => {
    const baseCollectionId = CollectionUtil.getBaseCollectionId();
    await CollectionUtil.loadCollectionsInView(baseCollectionId);
    ViewUtil.switchToBinId(baseCollectionId, ViewUtil.getActiveSubTab());
    Store.put(COLLECTION_ID, baseCollectionId);
    Store.put(SELECTED_COLLECTION, null);
  };

  onCollClick = async (collectionId, i: ?number) => {
    let pwd_collections = ViewUtil.getCollectionPwd().slice(0, i + 1);
    if (collectionId > 0) {
      const collection = await CollectionUtil.fetchCollection(collectionId);
      await CollectionUtil.switchToCollection(collection, pwd_collections);
    }
    else {
      await this.onBaseCollClick();
    }
  };

  renderCollCrumb = (collId: number, label: string, i: ?number) => {
    const pwd = ViewUtil.getCollectionPwd();
    const isLast = i === pwd.length - 1;
    const props: Object = {
      key: collId,
      className: 'crumb',
      icon: isLast ? null : 'images/icons_bin.svg'
    };

    if (!isLast) {
      props.onClick = (event) => this.onCollClick(collId, i);
    }
    return (
      <div className='crumb' onClick={props.onClick} key={'crumb - ' + collId}>
        <div className='crumb-icon'><img src={props.icon}></img></div>
        {/*<TextWithEllipsisFileExtension
          text={label}
          title={label}
          className='crumb-label'
          responsive={false}
        />*/}
        <ToolTip className='tooltip-crumb' text={label} >
          <div className='crumb-label'>
            {label}
          </div>
        </ToolTip>
      </div>
    )
  };

  renderCollCrumbs = () => {
    const crumbCollections = Store.get(BREADCRUMB_COLLECTIONS);
    const crumbs = crumbCollections ? crumbCollections.map((collection, i) => {
      const sep = (
        <span key={'collection-bread-crumb-' + collection.getCollectionId()} className="separator">
          <img src="images/arrow_down.svg" />
        </span>
      );

      const crumb = this.renderCollCrumb(collection.getCollectionId(), collection.getName(), i);

      return [sep, crumb];
    }) : [];

    return _.flatten(crumbs);
  };

  render() {
    //const { collection, PWD: pwd = [] } = this.props;
    const { collection, showFolderNavView } = this.props;
    const pwd = !collection ? ViewUtil.getPwd() : ViewUtil.getCollectionPwd();
    const baseProps: Object = {
      className: 'base',
      onClick: collection ? this.onBaseCollClick : this.onBaseClick
    };
    const base = (
      <span {...baseProps}>
        {
          collection
            ? 'Collections'
            : Store.get(ALL_FILES_OPTION) == 'SHOW_ALL_FILES'
              ? t('ALL_FILES')
              : t('ASSETS')
        }
      </span>
    );
    if (!pwd.length || (!collection && !showFolderNavView)) {
      return (
        <div name="Breadcrumb" className="single">
          {base}
        </div>
      );
    }

    return (
      <div name="Breadcrumb">
        {base}
        {collection ? this.renderCollCrumbs() : this.renderCrumbs()}
      </div>
    );
  }
}
